import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthUser } from '@/lib/types/auth-user'
import { getAvailableRoles } from '@/lib/auth/role-switcher'

/**
 * zustand persist の migrate ハンドラー（テスト容易化のため export）
 *
 * - v3未満: better-auth 移行のためユーザーデータをクリア（再ログイン必要）
 * - v3 → v4: 永続化された isAuthenticated を破棄（次回 session check で再評価）
 *
 * @internal Issue #843 で回帰テスト対象
 */
export function migrateAuthPersistedState(
  persistedState: unknown,
  version: number
): {
  user: AuthUser | null
  activeRole: string | null
  isImpersonating: boolean
  impersonateTargetName: string | null
} {
  if (version < 3) {
    return { user: null, activeRole: null, isImpersonating: false, impersonateTargetName: null }
  }
  const state = persistedState as
    | {
        user?: AuthUser | null
        activeRole?: string | null
        isImpersonating?: boolean
        impersonateTargetName?: string | null
      }
    | null
    | undefined
  return {
    user: state?.user ?? null,
    activeRole: state?.activeRole ?? null,
    isImpersonating: state?.isImpersonating ?? false,
    impersonateTargetName: state?.impersonateTargetName ?? null,
  }
}

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  // zustand persist の rehydrate 完了フラグ。SSR/hydration 競合時の制御に使う。
  // isAuthenticated は永続化しないため、別途 hydrate 状態を追跡する必要がある。
  isHydrated: boolean
  activeRole: string | null
  isImpersonating: boolean
  impersonateTargetName: string | null
  // セッション確認そのものが timeout/network error/500 等で「未確認」に終わったことを示す
  // （＝「確認できて未ログインだった」とは区別する・GF-D-08）。true の間は
  // AuthProvider の「未認証なら/loginへ送る」判定を止め、middleware が既に確認した
  // cookie の有効性を信じて画面を出したままにする。setUser() で必ず false に戻る。
  sessionCheckErrored: boolean
  setUser: (user: AuthUser | null) => void
  setLoading: (loading: boolean) => void
  setSessionCheckErrored: (errored: boolean) => void
  setActiveRole: (role: string) => void
  updateAvailableRoles: (roles: string[] | null) => void
  setImpersonateStatus: (isImpersonating: boolean, targetName: string | null) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isHydrated: false,
      activeRole: null,
      isImpersonating: false,
      impersonateTargetName: null,
      sessionCheckErrored: false,
      setUser: (user: AuthUser | null) =>
        set((state) => {
          if (!user)
            return {
              user: null,
              isAuthenticated: false,
              isLoading: false,
              activeRole: null,
              sessionCheckErrored: false,
            }
          const validRoles = getAvailableRoles(user.role, user.availableRoles)
          const preserved = state.activeRole && validRoles.includes(state.activeRole)
          return {
            user,
            isAuthenticated: true,
            isLoading: false,
            activeRole: preserved ? state.activeRole : (user.role ?? null),
            sessionCheckErrored: false,
          }
        }),
      setLoading: (loading) => set({ isLoading: loading }),
      setSessionCheckErrored: (errored) => set({ sessionCheckErrored: errored }),
      setActiveRole: (role) => set({ activeRole: role }),
      updateAvailableRoles: (roles) =>
        set((state) => {
          if (!state.user) return {}
          return { user: { ...state.user, availableRoles: roles } }
        }),
      setImpersonateStatus: (isImpersonating, targetName) =>
        set({
          isImpersonating,
          impersonateTargetName: targetName,
        }),
      logout: () =>
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          activeRole: null,
          isImpersonating: false,
          impersonateTargetName: null,
          sessionCheckErrored: false,
        }),
    }),
    {
      name: 'ghouse-auth',
      version: 5, // v5: isImpersonating/impersonateTargetName を永続化（リロード後もバナーを維持）
      partialize: (state) => ({
        user: state.user,
        // isAuthenticated は永続化しない（サーバーセッションを真実とする）。
        // 永続化すると expired session でもクライアント側が「認証済み」と誤認し、
        // 401 エラーがトーストとして見える事故を引き起こす。
        activeRole: state.activeRole,
        isImpersonating: state.isImpersonating,
        impersonateTargetName: state.impersonateTargetName,
      }),
      onRehydrateStorage: () => (state) => {
        // rehydrate 完了を記録。AuthProvider の session check 待機制御に使う。
        if (state) {
          state.isHydrated = true
        }
      },
      migrate: migrateAuthPersistedState,
    }
  )
)
